import { React, useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Button, Snackbar, Box, TextField, Radio, RadioGroup, FormControlLabel,
    Checkbox, IconButton,
    Autocomplete, Tooltip, Chip, Typography, Select, MenuItem, FormControl,
    InputLabel, Alert, DialogTitle, DialogContent, DialogActions, Card, CardContent,
    CardActionArea, Collapse
} from '@mui/material';
import ActionButton from '../../lib/ActionButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import { generateToken } from '../../lib/apiRequests';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useGetDatasetsQuery } from '../../state/apiSlice';

import styles from './MyJWTs.module.css';

import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.secondary.main,
    ...theme.typography.body2,
    padding: theme.spacing(1),
    color: theme.palette.text.main
}));

const EXPIRY_PRESETS = [
    { label: '7 days', value: 7 },
    { label: '30 days', value: 30 },
    { label: '90 days', value: 90 },
    { label: '365 days', value: 365 },
];

function getApiPaths(datasetId) {
    const base = `/v1/dataset/${datasetId}`;
    return [
        { path: `${base}/files/*`, label: 'Files (list, upload, download, delete)' },
        { path: `${base}/extrafiles/*`, label: 'Extra files' },
        { path: '/v1/dataset', label: 'List datasets (exact)' },
        { path: `${base}/mqtt/*`, label: 'MQTT setup, status, token refresh, alert' },
        { path: `${base}/accessRequests/*`, label: 'Access requests' },
        { path: `${base}/roles/*`, label: 'Role management' },
    ];
}

const DEFAULT_API_PATHS = [
    { pathIndex: 0, r: true, w: false },  // files/*
    { pathIndex: 1, r: true, w: false },  // extrafiles/*
    { pathIndex: 2, r: true, w: false },  // /v1/dataset (listing)
];

const canWrite = (role) => ['owner', 'admin'].includes(role);
const getRole = (ds) => ds?.AccessRole || 'none';
const isOpen = (ds) => ds?.AccessType === 'open';
const hasAccess = (ds) => getRole(ds) !== 'none' || isOpen(ds);

const UPLOAD_TOOLTIP = 'Single column:\n  Topic: upload/v1/<column>/<unit>/<type>\n  Payload: {"ts": 170904..., "value": 25.3}\n\nMulti-column:\n  Topic: upload/v1\n  Payload: {"ts": 170904..., "columns": [\n    {"column": "temp", "unit": "C", "type": "float", "value": 25.3}\n  ]}\n\nBatch (array of rows):\n  Topic: upload/v1\n  Payload: [{"ts": ..., "columns": [...]}, ...]\n\n# wildcard subscribes to all subtopics.';

const CONTROL_TOOLTIP = 'Send control signals OUT of the portal (close-the-loop / actuation).\n\nControl topics are open: any client authenticated to this dataset\'s broker may publish and subscribe on any control/v1/... topic, e.g.:\n  control/v1/<your-username>/#\n  control/v1/#\n\nOnly upload/v1 (data ingestion) is restricted.';

/**
 * Default MQTT topic rows for a freshly-selected dataset. Always offers:
 *  - upload/v1/#  (ingest; read on by default)
 *  - the caller's control topic (control signals OUT; read AND write OFF by
 *    default so the user opts in). Regular users get control/v1/<username>/#;
 *    owners/admins get the whole control/v1/# tree.
 */
const defaultMqttTopics = (role, username) => {
    const controlTopic = canWrite(role)
        ? 'control/v1/#'
        : (username ? `control/v1/${username}/#` : 'control/v1/<username>/#');
    return [
        { topic: 'upload/v1/#', r: true, w: false, label: 'Incoming live data', tooltip: UPLOAD_TOOLTIP },
        { topic: controlTopic, r: false, w: false, label: 'Outgoing control signals', tooltip: CONTROL_TOOLTIP },
    ];
};

export default function MyJWTs({ onClose }) {
    const { data: datasets } = useGetDatasetsQuery();
    const selectedID = useSelector(state => state.dataset.selectedID);
    const username = useSelector(state => state.login.user?.username || '');

    const preselectedDs = useMemo(() => {
        if (!datasets || selectedID < 0) return null;
        return datasets.filtered.find(ds => ds.DatasetID === selectedID) || null;
    }, [datasets, selectedID]);

    const [selectedDs, setSelectedDs] = useState(preselectedDs);
    const [tokenName, setTokenName] = useState('');
    const [accessLevel, setAccessLevel] = useState('r');
    const [apiEnabled, setApiEnabled] = useState(true);
    const [mqttSubscribe, setMqttSubscribe] = useState(true);
    const [mqttPublish, setMqttPublish] = useState(false);
    const [expiryDays, setExpiryDays] = useState(30);
    const [mytoken, setMytoken] = useState();
    const [snackOpen, setSnackOpen] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);

    // Advanced state
    const [apiPaths, setApiPaths] = useState(DEFAULT_API_PATHS);
    const [mqttTopics, setMqttTopics] = useState(
        () => defaultMqttTopics(getRole(preselectedDs), username)
    );

    // Derived
    const dsId = selectedDs?.DatasetID;
    const role = selectedDs ? getRole(selectedDs) : 'none';
    const dsIsOpen = selectedDs ? isOpen(selectedDs) : false;
    const writeAllowed = canWrite(role);
    const readAllowed = role !== 'none' || dsIsOpen;
    const allApiPaths = useMemo(() => dsId ? getApiPaths(dsId) : [], [dsId]);
    const isUnscopedMode = selectedDs !== null && selectedDs?.DatasetID === null;  // explicitly chose UNSCOPED_OPTION
    const canGenerate = isUnscopedMode || (Boolean(dsId) && (apiEnabled || mqttSubscribe || mqttPublish));

    // Auto-generate token name
    const autoName = useMemo(() => {
        if (!dsId) return '';
        return `token-ds${dsId}`;
    }, [dsId]);

    const effectiveName = tokenName || autoName;

    // When dataset changes, reset state
    const handleDatasetChange = (_, ds) => {
        setSelectedDs(ds);
        setMytoken(undefined);
        setAccessLevel('r');
        setMqttPublish(false);
        setApiPaths(DEFAULT_API_PATHS);
        setMqttTopics(defaultMqttTopics(getRole(ds), username));
    };

    // Build scopes and generate
    async function handleGenerate() {
        if (!canGenerate) {
            return;
        }
        setGenerating(true);
        try {
            const options = {};
            if (effectiveName) options.name = effectiveName;

            if (dsId && apiEnabled) {
                const mappedApi = apiPaths
                    .filter(p => p.r || p.w)
                    .map(p => {
                        const acc = (p.r && p.w) ? 'rw' : p.w ? 'w' : 'r';
                        return { path: allApiPaths[p.pathIndex].path, acc };
                    });
                if (mappedApi.length > 0) {
                    options.api = mappedApi;
                }
            }

            if (dsId && (mqttSubscribe || mqttPublish)) {
                const mappedMqtt = mqttTopics
                    .filter(t => (t.r || t.w) && (t.w ? canPublishTopic(t.topic) : true))
                    .map(t => {
                        let acc = 0;
                        if (t.r) acc |= 5;
                        if (t.w) acc |= 2;
                        return { datasetId: String(dsId), topic: t.topic, acc };
                    });
                if (mappedMqtt.length > 0) {
                    options.mqtt = mappedMqtt;
                }
            }

            const res = await generateToken(`${expiryDays}d`, options);
            setMytoken(res.data.access_token);
        } catch (err) {
            console.error('Token generation failed:', err);
            // Surface the server's actual error (e.g. "No access to dataset 41",
            // "openaccess role cannot request write access ..."). Falls back to
            // a generic message only when there's no response payload — e.g.
            // network failure or CORS preflight rejection.
            const serverMsg = err?.response?.data;
            const detail = (typeof serverMsg === 'string' && serverMsg.trim())
                ? serverMsg
                : (err?.message || 'unknown error');
            alert(`Failed to generate token: ${detail}`);
        }
        setGenerating(false);
    }

    // API path management
    const addApiPath = (_, option) => {
        if (!option) return;
        const idx = allApiPaths.findIndex(p => p.path === option.path);
        if (idx >= 0 && !apiPaths.find(p => p.pathIndex === idx)) {
            setApiPaths([...apiPaths, { pathIndex: idx, r: true, w: false }]);
        }
    };

    const removeApiPath = (i) => setApiPaths(apiPaths.filter((_, j) => j !== i));

    const toggleApiPathAccess = (i, field) => {
        const updated = [...apiPaths];
        updated[i] = { ...updated[i], [field]: !updated[i][field] };
        setApiPaths(updated);
    };

    // MQTT topic management
    const [newTopic, setNewTopic] = useState('');
    const addMqttTopic = () => {
        const t = newTopic.trim();
        if (t && !mqttTopics.find(mt => mt.topic === t)) {
            setMqttTopics([...mqttTopics, { topic: t, r: true, w: false }]);
            setNewTopic('');
        }
    };

    const removeMqttTopic = (i) => setMqttTopics(mqttTopics.filter((_, j) => j !== i));

    // Whether the current user is allowed to request publish (acc bit 2) on a
    // given topic. Owners/admins may publish anywhere; a regular user may
    // publish to any control/v1 topic (control topics are open). This mirrors
    // the server-side issuance policy in wcibapi/libs/mqttTopicPolicy.js.
    const canPublishTopic = (topic) => {
        if (writeAllowed) return true;
        return topic === 'control/v1' || topic.startsWith('control/v1/');
    };

    // A non-owner/admin user can still publish — but only to control topics.
    // This gates whether the MQTT-publish controls are offered at all.
    const canPublishControlOnly = !writeAllowed && Boolean(username);

    // Rebuild the default topic rows when the selected dataset's role or the
    // username resolves (datasets load asynchronously, so a preselected dataset
    // may not have a known role at first render). Only resets while the user
    // hasn't generated a token yet, to avoid clobbering manual edits mid-flow.
    useEffect(() => {
        if (selectedDs && selectedDs.DatasetID != null && !mytoken) {
            setMqttTopics(defaultMqttTopics(getRole(selectedDs), username));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDs, username]);

    const toggleMqttAccess = (i, field) => {
        const updated = [...mqttTopics];
        updated[i] = { ...updated[i], [field]: !updated[i][field] };
        setMqttTopics(updated);
    };

    // Summary text
    const summaryText = useMemo(() => {
        if (!selectedDs) return '';
        const parts = [`Scoped to dataset ${dsId} (${selectedDs.DatasetName})`];
        parts.push(accessLevel === 'rw' ? 'read + write access' : 'read-only access');
        if (apiEnabled) parts.push('API access');
        if (mqttSubscribe) parts.push('MQTT subscribe');
        if (mqttPublish) parts.push('MQTT publish');
        parts.push(`expires in ${expiryDays} days`);
        return parts.join(', ') + '.';
    }, [selectedDs, dsId, accessLevel, apiEnabled, mqttSubscribe, mqttPublish, expiryDays]);

    // Dataset grouping for autocomplete
    const UNSCOPED_OPTION = { DatasetID: null, DatasetName: 'No scope — full access token', AccessRole: 'unscoped', AccessType: 'unscoped', group: 'Token type' };

    const groupedDatasets = useMemo(() => {
        if (!datasets) return [UNSCOPED_OPTION];
        const ds = datasets.filtered.map(ds => ({
            ...ds,
            group: getRole(ds) !== 'none'
                ? 'Your datasets'
                : isOpen(ds) ? 'Open datasets' : 'Other'
        })).filter(ds => hasAccess(ds));
        return [UNSCOPED_OPTION, ...ds];
    }, [datasets]);

    const curlExample = useMemo(() => {
        if (!mytoken) return '';
        const path = dsId ? `/v1/dataset/${dsId}/files` : '/v1/dataset';
        const apiUrl = window._env_?.REACT_APP_WCIBAPI_URL || '';
        const base = apiUrl.startsWith('http') ? apiUrl : `${window.location.origin}${apiUrl}`;
        return `curl -H "Authorization: Bearer ${mytoken}" \\\n  ${base}${path}`;
    }, [mytoken, dsId]);

    return (<>
        <DialogTitle sx={{ bgcolor: 'rgb(43, 41, 41)', color: '#ffffff' }}>Generate Access Token</DialogTitle>
        <DialogContent dividers>
            {/* Token name */}
            <TextField
                label="Token name"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                placeholder={autoName}
                helperText="Identifies this token"
                fullWidth
                size="small"
                sx={{ mb: 2 }}
            />

            {/* Dataset selector */}
            <Autocomplete
                options={groupedDatasets}
                groupBy={(option) => option.group}
                getOptionLabel={(ds) => ds.DatasetID === null ? ds.DatasetName : `${ds.DatasetID} — ${ds.DatasetName}`}
                value={selectedDs}
                onChange={(_, ds) => {
                    handleDatasetChange(_, ds);
                }}
                isOptionEqualToValue={(opt, val) => opt.DatasetID === val.DatasetID}
                renderOption={(props, ds) => (
                    <li {...props} key={ds.DatasetID ?? 'unscoped'}>
                        <span className={styles['ds-option']}>
                            {ds.DatasetID === null
                                ? <span>⚠️ {ds.DatasetName}</span>
                                : <><span>{isOpen(ds) ? '🌐' : '🔒'} {ds.DatasetID} — {ds.DatasetName}</span>
                                    <Chip label={getRole(ds) === 'none' ? 'open' : getRole(ds)} size="small"
                                        className={styles['role-chip']} /></>
                            }
                        </span>
                    </li>
                )}
                renderInput={(params) => <TextField {...params} label="Dataset" size="small" />}
                sx={{ mb: 1 }}
            />

            {selectedDs && (
                <Typography variant="body2" className={styles['role-info']}>
                    Your role: <strong>{role === 'none' && dsIsOpen ? 'none (open access — read only)' : role}</strong>
                </Typography>
            )}

            {selectedDs && !readAllowed && (
                <Typography color="error" sx={{ mt: 1 }}>No access to this dataset.</Typography>
            )}

            {selectedDs && readAllowed && (<>
                {/* Access level */}
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">Access level:</Typography>
                    <RadioGroup row value={accessLevel} onChange={(e) => {
                        setAccessLevel(e.target.value);
                        if (e.target.value === 'r') {
                            // "Read only" clears dataset-write scopes, but a user may
                            // still publish to control topics — that's not a
                            // dataset-write grant, so keep w on topics they may publish to.
                            if (!canPublishControlOnly) {
                                setMqttPublish(false);
                            }
                            setApiPaths(apiPaths.map(p => ({ ...p, w: false })));
                            setMqttTopics(mqttTopics.map(t => ({ ...t, w: t.w && canPublishTopic(t.topic) })));
                        }
                    }}>
                        <FormControlLabel value="r" control={<Radio size="small" />} label="Read only" />
                        <Tooltip title={!writeAllowed ? 'Requires admin or owner role' : ''}>
                            <FormControlLabel value="rw" control={<Radio size="small" />}
                                label="Read + Write" disabled={!writeAllowed} />
                        </Tooltip>
                    </RadioGroup>
                </Box>

                {/* Permissions */}
                <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2">Permissions</Typography>
                    <FormControlLabel control={
                        <Checkbox checked={apiEnabled} onChange={(e) => setApiEnabled(e.target.checked)} size="small" />
                    } label="API access (list/download files)" />
                    <FormControlLabel control={
                        <Checkbox checked={mqttSubscribe} onChange={(e) => setMqttSubscribe(e.target.checked)} size="small" />
                    } label="MQTT subscribe (subscribe to and receive messages)" />
                    <Tooltip title={!writeAllowed
                        ? (canPublishControlOnly
                            ? 'You can publish to control topics: control/v1/#'
                            : 'Requires admin or owner role')
                        : ''}>
                        <FormControlLabel control={
                            <Checkbox checked={mqttPublish}
                                disabled={!(writeAllowed || canPublishControlOnly) || (accessLevel === 'r' && writeAllowed)}
                                onChange={(e) => setMqttPublish(e.target.checked)} size="small" />
                        } label="MQTT publish (send messages to the broker)" />
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -0.5 }}>
                        {writeAllowed
                            ? 'Publish to upload/v1/# (ingest) or control/v1/# (control signals out). Enable per topic under Advanced; off by default.'
                            : canPublishControlOnly
                                ? 'You may publish control signals to control/v1/# — enable read/write for it under Advanced (off by default).'
                                : 'Publishing requires an owner or admin role on this dataset.'}
                    </Typography>
                </Box>

                {/* Advanced */}
                <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardActionArea onClick={() => setAdvancedOpen(!advancedOpen)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2">
                                    Advanced — restrict paths & topics
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Fine-tune which API endpoints and MQTT topics this token can access
                                </Typography>
                            </Box>
                            <ExpandMoreIcon sx={{
                                transform: advancedOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                            }} />
                        </Box>
                    </CardActionArea>
                    <Collapse in={advancedOpen}>
                        <CardContent>
                            {apiEnabled && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>API Paths</Typography>
                                {apiPaths.map((p, i) => (
                                    <div key={i} className={styles['scope-row']}>
                                        <Typography variant="body2" className={styles['scope-path']}>
                                            {allApiPaths[p.pathIndex]?.path}
                                        </Typography>
                                        <span className={styles['scope-label']}>
                                            {allApiPaths[p.pathIndex]?.label}
                                        </span>
                                        <span className={styles['scope-buttons']}>
                                            <Tooltip title="Read — allows GET requests">
                                                <Button size="small" variant={p.r ? 'contained' : 'outlined'}
                                                    className={styles['acc-btn']}
                                                    onClick={() => toggleApiPathAccess(i, 'r')}>r</Button>
                                            </Tooltip>
                                            <Tooltip title={!writeAllowed ? 'Your role does not allow write access' : 'Write — allows POST, PUT, DELETE requests'}>
                                                <span>
                                                    <Button size="small" variant={p.w ? 'contained' : 'outlined'}
                                                        className={styles['acc-btn']}
                                                        disabled={!writeAllowed || accessLevel === 'r'}
                                                        onClick={() => toggleApiPathAccess(i, 'w')}>w</Button>
                                                </span>
                                            </Tooltip>
                                            <IconButton size="small" onClick={() => removeApiPath(i)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </div>
                                ))}
                                <Autocomplete
                                    options={allApiPaths.filter((_, i) => !apiPaths.find(p => p.pathIndex === i))}
                                    getOptionLabel={(o) => `${o.path} — ${o.label}`}
                                    onChange={addApiPath}
                                    value={null}
                                    renderInput={(params) => <TextField {...params} label="+ Add path" size="small" />}
                                    size="small"
                                    sx={{ mt: 1 }}
                                />
                            </Box>
                        )}
                            {(mqttSubscribe || mqttPublish) && (
                            <Box>
                                <Alert severity="info" sx={{ mb: 1.5 }}>
                                    Tip: when troubleshooting an MQTT publish that &ldquo;looks fine&rdquo; but no message arrives, use QoS &ge; 1. QoS 0 messages are fire-and-forget per the MQTT spec — the broker silently drops them on auth/ACL failures, with no feedback to the client.
                                </Alert>
                                <Alert severity="info" sx={{ mb: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                        Topic publish rules
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                                        <li>
                                            <code>upload/v1/#</code> — ingest data IN. Subscribe is open to
                                            any role with read access; <strong>publish requires owner or admin</strong> (checked on every publish, in addition to the token&apos;s write bit).
                                        </li>
                                        <li>
                                            <code>control/v1/#</code> — control signals / triggers.
                                            <strong>Open to any user</strong>: publish and subscribe on any
                                            <code>control/v1/...</code> topic
                                            {username ? <> (e.g. <code>control/v1/{username}/#</code>)</> : null}.
                                        </li>
                                        <li>
                                            <strong>Any other topic</strong> — read/subscribe can be granted on
                                            any topic you have dataset access to; publishing there requires owner or admin.
                                        </li>
                                    </ul>
                                </Alert>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>MQTT Topics</Typography>
                                {mqttTopics.map((t, i) => (
                                    <div key={i} className={styles['scope-row']}>
                                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{t.tooltip}</span>} placement="bottom-start">
                                            <Typography variant="body2" className={styles['scope-path']}>
                                                {t.topic}
                                            </Typography>
                                        </Tooltip>
                                        {t.label && (
                                            <span className={styles['scope-label']}>
                                                {t.label}
                                            </span>
                                        )}
                                        <span className={styles['scope-buttons']}>
                                            <Tooltip title="Subscribe + receive — allows SUBSCRIBE and per-message delivery (acc bit 1+4 = 5)">
                                                <Button size="small" variant={t.r ? 'contained' : 'outlined'}
                                                    className={styles['acc-btn']}
                                                    onClick={() => toggleMqttAccess(i, 'r')}>r</Button>
                                            </Tooltip>
                                            <Tooltip title={!canPublishTopic(t.topic) ? 'You may only publish to control/v1/... topics; other topics require owner or admin' : 'Publish — allows the client to publish messages to this topic (acc bit 2)'}>
                                                <span>
                                                    <Button size="small" variant={t.w ? 'contained' : 'outlined'}
                                                        className={styles['acc-btn']}
                                                        disabled={!canPublishTopic(t.topic) || (accessLevel === 'r' && writeAllowed)}
                                                        onClick={() => toggleMqttAccess(i, 'w')}>w</Button>
                                                </span>
                                            </Tooltip>
                                            <IconButton size="small" onClick={() => removeMqttTopic(i)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </div>
                                ))}
                                <div className={styles['add-topic-row']}>
                                    <TextField label="+ Add topic" size="small" value={newTopic}
                                        onChange={(e) => setNewTopic(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && addMqttTopic()}
                                        placeholder="upload/v1/sensors"
                                        sx={{ flex: 1 }} />
                                    <ActionButton size="small" onClick={addMqttTopic} disabled={!newTopic.trim()}>Add</ActionButton>
                                </div>
                            </Box>
                        )}
                        </CardContent>
                    </Collapse>
                </Card>

                {/* Summary */}
                <Box className={styles['summary-box']}>
                    <Typography variant="body2">{summaryText}</Typography>
                </Box>
            </>)}

            {!selectedDs && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Select a dataset above to create a scoped token, OR choose &lsquo;No scope&rsquo; explicitly
                    from the dropdown to generate an unscoped token.
                </Alert>
            )}

            {isUnscopedMode && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    No dataset selected — this will create an unscoped token with full access
                    to all datasets and actions your account has permissions for.
                    If this token is leaked, it can be used to access everything you can.
                    Select a dataset above to create a scoped token instead.
                </Alert>
            )}

            {/* Expiry */}
            <Box sx={{ mt: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Expires in</InputLabel>
                    <Select value={expiryDays} label="Expires in"
                        onChange={(e) => setExpiryDays(e.target.value)}>
                        {EXPIRY_PRESETS.map(p => (
                            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body2" className={styles['expiry-date']}>
                    Expires: {new Date(Date.now() + expiryDays * 86400000).toLocaleDateString()}
                </Typography>
            </Box>

            {/* Token output */}
            {mytoken && (
                <Item className={styles['jwt-access-token']}>
                    {mytoken}
                </Item>
            )}
        </DialogContent>

        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            {!canGenerate && (
                <Alert severity="info" sx={{ my: 1, width: '100%' }}>
                    {!selectedDs
                        ? "Select a dataset above, or choose 'No scope' explicitly to generate an unscoped token."
                        : !dsId
                            ? "Select a dataset above to generate a scoped token."
                            : 'Enable at least one of API or MQTT access below.'}
                </Alert>
            )}
            <ActionButton variant="outlined" onClick={handleGenerate} loading={generating} disabled={!canGenerate}>
                {generating ? 'Generating...' : 'Generate token'}
            </ActionButton>
            {onClose && (
                <Button variant="outlined" onClick={onClose}>Close</Button>
            )}
            <Box sx={{ flex: 1 }} />
            {mytoken && (<>
                <CopyToClipboard text={mytoken}>
                    <ActionButton size="small" variant="outlined"
                        onClick={() => setSnackOpen(true)}>📋 Copy</ActionButton>
                </CopyToClipboard>
                <CopyToClipboard text={curlExample}>
                    <ActionButton size="small" variant="outlined"
                        onClick={() => setSnackOpen(true)}>📄 Copy as curl</ActionButton>
                </CopyToClipboard>
            </>)}
        </DialogActions>

        <Snackbar open={snackOpen} autoHideDuration={3000}
            onClose={() => setSnackOpen(false)} message="Copied to clipboard" />
    </>);
}

MyJWTs.propTypes = {
    onClose: PropTypes.func,
};
